// src/lib/data-projection.js — WAGON M (Lots M3.2 + M3.3)
//
// PROJECTION PURE du blob `data` KV (V1) pour la facturation des logements
// basculés en source 'v2' (sync Beds24 relationnel). Module PUR : ZÉRO I/O.
// L'I/O (lecture des rows reservations) est faite en amont par le hook
// `use-resas-v2.js` ; ici on ne fait que recomposer un `data` projeté.
//
// PRINCIPE (F-M3-2, correction pessimist actée Dimitri) :
//   La projection se fait EN AMONT — pas dans le seul wrapper `calc`. On
//   produit un `data` PROJETÉ unique que TOUS les lecteurs de calcul/affichage
//   compta consomment (wrapper calc, calcSousLocForLog, allocSousLocCharges,
//   vues). Conséquence : un seul point de substitution, comportement cohérent
//   partout, aucune dérive entre call-sites.
//
// CONTRAT DE SÛRETÉ (zéro régression prouvable) :
//   - `v2Lids` vide → retourne le `data` ORIGINAL (MÊME RÉFÉRENCE, ===). Tout
//     le flux aval reçoit l'objet identique → 0 régression mécanique. C'est
//     l'état prod actuel (aucun logement basculé).
//   - On ne remplace QUE `data[mk][lid].reservations`, et UNIQUEMENT pour les
//     lid ∈ v2Lids sur un mois NON GELÉ. loyer / chargesVar / extras /
//     ajustements et toute autre clé du mois restent INTOUCHÉS (la sous-loc
//     lc1/lc2/lc3 garde son loyer KV porté par le log parent).
//   - Copies shallow CIBLÉES : on ne mute JAMAIS l'objet `data` d'origine ni
//     ses sous-objets (on clone uniquement les niveaux touchés).
//
// GEL CUT-OVER (M3.3) :
//   Un mois (lid × mk) dont la facture commission proprio (série P) est déjà
//   émise est FIGÉ : pas de projection, le `data` KV de ce mois reste tel quel
//   (une facture émise a snapshoté ses lignes — on ne réécrit jamais le passé).
//   Détection via `findBlockingCommission(invoicesRaw, { logId, monthLabel })`.
//   ⚠️ Pour une CHAMBRE sous-loc (lc1/lc2/lc3) il n'existe AUCUNE facture P
//   (les factures P sont liées au logement parent l1-l7) → le gel est « sans
//   objet » mais le code reste générique (findBlockingCommission renvoie null
//   → projection autorisée). Documenté : « mois facturé = figé ».
//
// OVERRIDES MÉNAGE (F-M3-5) :
//   Un override ménage manuel n'est appliqué QUE si la résa V2 porte une clé
//   stable (airbnbCode pour Airbnb, bookingRef pour Booking = apiReference).
//   JAMAIS de match par fallback (checkIn, plateforme) : une résa sans code
//   stable n'est jamais overridée (risque de collision destructive).

import { getResasForMonth } from './resas-v2-source'
import {
  findBlockingCommission,
  findBlockingVoyageurInvoiceForMonth,
} from './invoice-guard'
import {
  computeInvoiceMonthByNights,
  nightsInMonth,
} from './resa-invoice-month'
import { resolveProrataParts } from './prorata-resolve'
import { computeMonthsTouched } from './resa-prorata-splits'
import { collectCsvApresBascule, codesReprisParLeCsv, codeV2DeLaLigne } from './csv-bascule'

// ── applyMenageOverrides(resas, overrides) ──
// `overrides` : objet { [codeStable]: number } indexé UNIQUEMENT par clé stable
// (apiReference = airbnbCode | bookingRef). Retourne un NOUVEAU tableau de résas
// (copies shallow des seules résas overridées) ; les autres références sont
// préservées. Une résa sans code stable, ou dont le code n'est pas dans
// `overrides`, est renvoyée TELLE QUELLE (même référence).
//
// Le ménage est la SEULE valeur surchargée — le reste de la résa est intouché.
export function applyMenageOverrides(resas, overrides) {
  if (!Array.isArray(resas) || resas.length === 0) return resas
  if (!overrides || typeof overrides !== 'object') return resas
  let changed = false
  const out = resas.map((r) => {
    if (!r) return r
    // Clé stable uniquement (airbnbCode OU bookingRef) — jamais de fallback.
    const code = r.airbnbCode || r.bookingRef || null
    if (!code) return r
    if (!Object.prototype.hasOwnProperty.call(overrides, code)) return r
    const v = Number(overrides[code])
    if (!Number.isFinite(v)) return r
    changed = true
    return { ...r, menage: Math.round(v * 100) / 100, menage_source: 'override' }
  })
  return changed ? out : resas
}

// ── applyMontantOverrides(resas, overrides) ──
// MIROIR EXACT de applyMenageOverrides, mais surcharge le MONTANT NET HÔTE
// (`r.montant`) au lieu du ménage. La commission FBZ et le net proprio se
// recalculent automatiquement en aval (base = max(0, montant − menage)) — aucun
// lecteur de calc à modifier.
// `overrides` : objet { [codeStable]: number } indexé UNIQUEMENT par clé stable
// (apiReference = airbnbCode | bookingRef). Retourne un NOUVEAU tableau de résas
// (copies shallow des seules résas overridées) ; les autres références sont
// préservées. Une résa sans code stable, ou dont le code n'est pas dans
// `overrides`, est renvoyée TELLE QUELLE (même référence). REMPLACE, n'additionne pas.
export function applyMontantOverrides(resas, overrides) {
  if (!Array.isArray(resas) || resas.length === 0) return resas
  if (!overrides || typeof overrides !== 'object') return resas
  let changed = false
  const out = resas.map((r) => {
    if (!r) return r
    // Clé stable uniquement (airbnbCode OU bookingRef) — jamais de fallback.
    const code = r.airbnbCode || r.bookingRef || null
    if (!code) return r
    if (!Object.prototype.hasOwnProperty.call(overrides, code)) return r
    const v = Number(overrides[code])
    if (!Number.isFinite(v)) return r
    changed = true
    return { ...r, montant: Math.round(v * 100) / 100, montant_source: 'override' }
  })
  return changed ? out : resas
}

// ── collectMenageOverrides(data, v2Lids) ──
// Agrège les overrides ménage manuels stockés PAR MOIS dans le blob KV
// (data[mk][lid].menageOverrides = { [codeStable]: number }) en une map PAR LID
// (overrides[lid][codeStable] = number) — la forme attendue par
// projectDataForFacturation / applyMenageOverrides.
//
// SÛRETÉ : un codeStable (airbnbCode | bookingRef) est UNIQUE par résa, donc
// présent dans AU PLUS un mois → l'agrégation cross-mois ne peut pas écraser
// deux valeurs distinctes. On ne lit QUE les lid ∈ v2Lids (les autres logements
// n'ont pas de projection). v2Lids vide → {} (rien à agréger).
export function collectMenageOverrides(data, v2Lids) {
  if (!data || typeof data !== 'object') return {}
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {}
  const v2Set = new Set(v2Lids)
  const out = {}
  for (const mk of Object.keys(data)) {
    const month = data[mk]
    if (!month || typeof month !== 'object') continue
    for (const lid of Object.keys(month)) {
      if (!v2Set.has(lid)) continue
      const ov = month[lid] && month[lid].menageOverrides
      if (!ov || typeof ov !== 'object') continue
      if (!out[lid]) out[lid] = {}
      for (const code of Object.keys(ov)) {
        const v = Number(ov[code])
        if (Number.isFinite(v)) out[lid][code] = v
      }
    }
  }
  return out
}

// ── collectTombstonedCodes(data, v2Lids) ── (Lot 2 V2-delete-resa)
// Agrège les codes stables (airbnbCode | bookingRef) TOMBSTONÉS pour suppression
// V2, lus depuis le store EXISTANT data._tombstones[lid] (buckets airbnb_codes /
// booking_refs — M2 acté : réutilisation du même store que la sync iCal). Retourne
// une map PAR LID : { [lid]: Set<codeStable> }, UNIQUEMENT pour lid ∈ v2Lids.
//
// MIROIR de collectMenageOverrides : v2Lids vide → {}. Seuls les lid basculés 'v2'
// ont une projection, donc seuls leurs tombstones de code stable sont pertinents
// ici (le filtre suppression ne s'applique qu'aux résas projetées V2).
export function collectTombstonedCodes(data, v2Lids) {
  if (!data || typeof data !== 'object') return {}
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {}
  const tombs = data._tombstones
  if (!tombs || typeof tombs !== 'object') return {}
  const out = {}
  for (const lid of v2Lids) {
    if (!lid) continue
    const t = tombs[lid]
    if (!t || typeof t !== 'object') continue
    const set = new Set()
    if (Array.isArray(t.airbnb_codes)) {
      for (const e of t.airbnb_codes) {
        if (e && e.airbnbCode) set.add(String(e.airbnbCode))
      }
    }
    if (Array.isArray(t.booking_refs)) {
      for (const e of t.booking_refs) {
        if (e && e.bookingRef) set.add(String(e.bookingRef))
      }
    }
    if (set.size > 0) out[lid] = set
  }
  return out
}

// ── applyTombstones(resas, tombstonedSet, removedSink?) ── (Lot 2 V2-delete-resa)
// MIROIR de applyMenageOverrides/applyMontantOverrides mais RETIRE des résas au
// lieu de les surcharger. Une résa est retirée si son code stable
// (airbnbCode || bookingRef) ∈ tombstonedSet. Les résas gardées conservent leur
// référence (filter ne clone pas). Une résa SANS code stable n'est jamais retirée.
//
// Contrat « MÊME référence si rien changé » via FLAG `removed` (pas length) —
// pattern maison : un set vide / aucun match → return `resas` (===).
//
// `removedSink` (optionnel) : array où les résas retirées sont poussées (pour
// l'affichage admin grisé/barré + bouton Restaurer côté UI).
export function applyTombstones(resas, tombstonedSet, removedSink) {
  if (!Array.isArray(resas) || resas.length === 0) return resas
  if (!tombstonedSet || typeof tombstonedSet.has !== 'function' || tombstonedSet.size === 0) {
    return resas
  }
  let removed = false
  const kept = []
  for (const r of resas) {
    if (!r) { kept.push(r); continue }
    const code = r.airbnbCode || r.bookingRef || null
    if (code && tombstonedSet.has(String(code))) {
      removed = true
      if (Array.isArray(removedSink)) removedSink.push(r)
      continue // résa retirée de la facturation
    }
    kept.push(r)
  }
  return removed ? kept : resas
}

// ── collectMontantOverrides(data, v2Lids) ──
// MIROIR EXACT de collectMenageOverrides, mais agrège les overrides MONTANT
// (data[mk][lid].montantOverrides = { [codeStable]: number }) en une map PAR LID
// (overrides[lid][codeStable] = number) — la forme attendue par
// projectDataForFacturation / applyMontantOverrides.
export function collectMontantOverrides(data, v2Lids) {
  if (!data || typeof data !== 'object') return {}
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {}
  const v2Set = new Set(v2Lids)
  const out = {}
  for (const mk of Object.keys(data)) {
    const month = data[mk]
    if (!month || typeof month !== 'object') continue
    for (const lid of Object.keys(month)) {
      if (!v2Set.has(lid)) continue
      const ov = month[lid] && month[lid].montantOverrides
      if (!ov || typeof ov !== 'object') continue
      if (!out[lid]) out[lid] = {}
      for (const code of Object.keys(ov)) {
        const v = Number(ov[code])
        if (Number.isFinite(v)) out[lid][code] = v
      }
    }
  }
  return out
}

// ── collectMonthOverrides(data, v2Lids) ── (Lot 3 V2-move-resa)
// MIROIR EXACT de collectMontantOverrides, mais agrège les DÉPLACEMENTS de mois
// (data._monthOverrides[lid] = { [codeStable]: 'YYYY-MM' }) en une map PAR LID
// (out[lid][codeStable] = 'YYYY-MM') — la forme attendue par
// projectDataForFacturation pour la ré-imputation effective.
//
// DIFFÉRENCE de stockage : les overrides montant/ménage vivent PAR MOIS
// (data[mk][lid].xOverrides) ; les déplacements vivent dans UN store GLOBAL
// data._monthOverrides[lid] (comme les tombstones data._tombstones[lid]) — un
// code n'a qu'UNE cible de mois, indépendante du mois d'affichage. On ne lit QUE
// les lid ∈ v2Lids. Valeurs non 'YYYY-MM' ignorées (défense). v2Lids vide → {}.
export function collectMonthOverrides(data, v2Lids) {
  if (!data || typeof data !== 'object') return {}
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {}
  const store = data._monthOverrides
  if (!store || typeof store !== 'object') return {}
  const out = {}
  for (const lid of v2Lids) {
    if (!lid) continue
    const lidMap = store[lid]
    if (!lidMap || typeof lidMap !== 'object') continue
    const clean = {}
    let any = false
    for (const code of Object.keys(lidMap)) {
      const v = lidMap[code]
      if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v)) {
        clean[code] = v
        any = true
      }
    }
    if (any) out[lid] = clean
  }
  return out
}

// ── applyMonthMoves(baseResas, mk, lidMoveMap, projectedByCode) ── (Lot 3) ──
// RÉ-IMPUTATION EFFECTIVE pour un (lid, mk) donné : une résa appartient à `mk`
// SSI effectiveMk(code) === mk, avec effectiveMk = lidMoveMap[code] || naturalMk.
// Imputation EFFECTIVE UNIQUE (correction pessimist NP-1) — un seul prédicat,
// donc impossible d'être dans 2 mois ou aucun (pas de double-comptage, pas de
// fuite). Concrètement, en OUT + IN :
//   - OUT : retire de baseResas (déjà imputées au mois NATUREL `mk` + tombstone
//     filtré) toute résa dont le code a un override ≠ mk → elle part ailleurs.
//   - IN  : ajoute les résas dont l'override === mk mais dont le mois naturel
//     ≠ mk. Source = `projectedByCode` (Map code→résa projetée, construite UNE
//     fois par lid à partir des rows projetées à leur mois NATUREL PUIS filtrées
//     tombstone — une résa supprimée n'est JAMAIS réinjectée). Les entrantes sont
//     estampées { ...r, month_moved: mk, montant_source/menage_source clonés }.
//
// CONTRAT MÊME-RÉFÉRENCE (flag, pas length) : aucun override pour ce lid
// (lidMoveMap vide/absent) → renvoie baseResas (===). Une résa déjà entrante d'un
// autre code n'est jamais dupliquée (IN ne touche QUE projectedByCode, pas baseResas).
export function applyMonthMoves(baseResas, mk, lidMoveMap, projectedByCode) {
  if (!Array.isArray(baseResas)) return baseResas
  if (!lidMoveMap || typeof lidMoveMap !== 'object') return baseResas
  const codes = Object.keys(lidMoveMap)
  if (codes.length === 0) return baseResas

  let changed = false

  // OUT — retire les résas dont l'override pointe AILLEURS que mk.
  const kept = []
  for (const r of baseResas) {
    if (!r) { kept.push(r); continue }
    const code = r.airbnbCode || r.bookingRef || null
    const target = code ? lidMoveMap[code] : null
    if (target && target !== mk) {
      changed = true
      continue // part vers son mois cible (réinjectée là-bas par IN)
    }
    kept.push(r)
  }

  // IN — ajoute les résas dont l'override === mk mais dont le NATUREL ≠ mk.
  // Source = projectedByCode (déjà filtrée tombstone). On ne réinjecte que si
  // la résa n'est PAS déjà dans `kept` au mois naturel (cas override === naturel
  // exclu côté pose, mais défense : skip si naturalMk === mk car déjà présente).
  if (projectedByCode && typeof projectedByCode.get === 'function') {
    for (const code of codes) {
      if (lidMoveMap[code] !== mk) continue
      const entry = projectedByCode.get(code)
      if (!entry) continue // résa absente (tombstonée, ou row introuvable)
      // entry.naturalMk === mk → la résa est DÉJÀ dans baseResas (kept) ; ne pas
      // dupliquer (override redondant vers le naturel).
      if (entry.naturalMk === mk) continue
      changed = true
      const r = entry.resa
      // Estampille `month_moved` pour la pastille ↪ UI. `...r` préserve déjà
      // tous les champs (montant_source/menage_source seront posés APRÈS, par
      // les overrides Lot 1 qui s'appliquent par code → suivent la résa ici).
      kept.push({ ...r, month_moved: mk })
    }
  }

  return changed ? kept : baseResas
}

// ── collectManualResasForMonth(reservations, mk) ── (Lot 4 V2-create-manual) ──
// Extrait les réservations MANUELLES (créées à la main par l'admin sur un
// logement/chambre V2) d'un bucket physique `data[mk][lid].reservations`.
//
// DISCRIMINANT STRICT : `String(r.source) === 'manual'` (allowlist). JAMAIS
// `manual_origin` (12 parasites en base, posés par applyManualOriginFlag sur
// toute édition V1 — filtrer dessus serait une bombe). En base `source` = 0
// occurrence (vierge) ; seules les manuelles créées par createResaV2 le portent.
//
// IMPUTATION PAR BUCKET PHYSIQUE SEUL : les manuelles vivent dans le bucket `mk`
// par construction (createResaV2 push dans data[month][lid].reservations, pas de
// month_override). Le paramètre `mk` n'est là que pour la signature/cohérence ;
// aucune ré-imputation read-time (pas de resolveInvoiceMonth qui pourrait évaporer
// une résa). NE CLONE PAS : préserve les références (0 mutation de l'original).
export function collectManualResasForMonth(reservations, mk) {
  if (!Array.isArray(reservations) || !reservations.length) return []
  return reservations.filter((r) => r && String(r.source) === 'manual')
}

// ── collectProrataSplits(data, v2Lids) ── (Lot 5a PRORATA — 5e calque) ──
// MIROIR de collectMonthOverrides : agrège les specs de prorata par lid depuis
// le store GLOBAL data._prorataSplits[lid] = { [codeStable]: spec }, où spec =
//   { mode:'auto', excluded?:string[] } | { mode:'manual', amounts:{[mk]:number} }.
// Retourne une map PAR LID : out[lid][codeStable] = spec, UNIQUEMENT lid ∈ v2Lids.
//
// Le store est GLOBAL (comme _tombstones / _monthOverrides) : un code n'a qu'UNE
// spec de prorata, indépendante du mois d'affichage. On ne lit QUE les lid ∈
// v2Lids. v2Lids vide → {}. Store absent/vide → {} (INERTIE : 0 spec = 0 calque).
// Validation minimale du shape : mode ∈ {auto, manual}, sinon spec ignorée.
export function collectProrataSplits(data, v2Lids) {
  if (!data || typeof data !== 'object') return {}
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {}
  const store = data._prorataSplits
  if (!store || typeof store !== 'object') return {}
  const out = {}
  for (const lid of v2Lids) {
    if (!lid) continue
    const lidMap = store[lid]
    if (!lidMap || typeof lidMap !== 'object') continue
    const clean = {}
    let any = false
    for (const code of Object.keys(lidMap)) {
      const spec = lidMap[code]
      if (!spec || typeof spec !== 'object') continue
      if (spec.mode !== 'auto' && spec.mode !== 'manual') continue
      clean[code] = spec
      any = true
    }
    if (any) out[lid] = clean
  }
  return out
}

// ── applyProrataSplits(baseResas, mk, lidProrataResolved) ── (Lot 5a) ──
// Applique le 5e calque pour un (lid, mk) donné, APRÈS montantOverride et AVANT
// la fusion manuelle. `lidProrataResolved` = { [code]: { parts, naturalMk, resa } }
// PRÉ-RÉSOLU UNE FOIS PAR CODE hors boucle mk (BLOQUANT B2). `parts` peut être
// null (gel / incohérence / XOR move) → on NE TOUCHE PAS (résa entière reste sur
// son naturel).
//
// Pour chaque code dont parts !== null :
//   - mk === naturalMk → REMPLACE (par identité de code stable) la résa naturelle
//     par { ...resaNaturelle, montant: parts[mk], nuits: nightsInMonth(...) }.
//     menage/taxe d'origine PRÉSERVÉS (portés UNE seule fois, au naturel).
//   - mk !== naturalMk ET parts[mk] défini → AJOUTE une sous-ligne virtuelle
//     { ...resa, montant: parts[mk], menage:0, taxe:0, nuits: nightsInMonth(...) }.
//
// IMPÉRATIF anti-double-comptage : au mois naturel on REMPLACE (jamais push) ;
// le remplacement se fait par identité de code stable. Une résa proratisée
// apparaît dans N buckets avec un montant partiel chacun (Σ == total), mais
// menage/taxe UNE SEULE fois (au naturel).
//
// CONTRAT MÊME-RÉFÉRENCE (flag, pas length) : lidProrataResolved vide → renvoie
// baseResas (===) [INERTIE]. On ne clone le tableau que si une modif a lieu.
export function applyProrataSplits(baseResas, mk, lidProrataResolved) {
  if (!Array.isArray(baseResas)) return baseResas
  if (!lidProrataResolved || typeof lidProrataResolved !== 'object') return baseResas
  const codes = Object.keys(lidProrataResolved)
  if (codes.length === 0) return baseResas

  let changed = false
  let out = baseResas

  for (const code of codes) {
    const entry = lidProrataResolved[code]
    if (!entry) continue
    const { parts, naturalMk, resa } = entry
    // parts === null → fail-safe (gel / incohérent / XOR move) : NE RIEN faire,
    // la résa reste ENTIÈRE sur son mois naturel (déjà dans baseResas).
    if (!parts) continue

    if (mk === naturalMk) {
      // REMPLACER (par identité de code stable) la résa naturelle. Si le montant
      // au naturel n'est pas dans parts (mois naturel exclu en mode manual/auto),
      // on retire sa part d'argent en mettant 0 (la résa reste pour menage/taxe).
      const part = Object.prototype.hasOwnProperty.call(parts, mk) ? parts[mk] : 0
      let replaced = false
      const next = out.map((r) => {
        if (!r) return r
        const rcode = r.airbnbCode || r.bookingRef || null
        if (rcode && String(rcode) === String(code)) {
          replaced = true
          return {
            ...r,
            montant: part,
            nuits: nightsInMonth(r, mk),
            _prorataOf: String(code),
            _prorataMonth: mk,
          }
        }
        return r
      })
      if (replaced) {
        changed = true
        out = next
      }
    } else if (Object.prototype.hasOwnProperty.call(parts, mk)) {
      // AJOUTER une sous-ligne virtuelle (mois NON naturel) : montant partiel,
      // menage/taxe = 0 (portés UNE seule fois au naturel). Clone le tableau au
      // premier ajout pour ne JAMAIS muter baseResas (référence d'origine).
      if (out === baseResas) out = [...baseResas]
      changed = true
      out.push({
        ...resa,
        montant: parts[mk],
        menage: 0,
        taxe: 0,
        nuits: nightsInMonth(resa, mk),
        _prorataOf: String(code),
        _prorataMonth: mk,
      })
    }
  }

  return changed ? out : baseResas
}

// ── projectDataForFacturation(data, opts) ──
// Retourne un `data` projeté (ou le `data` original si v2Lids vide).
//
// @param {object} data — blob KV V1 : data[mk][lid] = { reservations, loyer,
//   chargesVar, extras, ajustements, ... }
// @param {object} opts
//   @param {string[]} opts.v2Lids — lids (logs feuilles + chambres sous-loc)
//     dont getFacturationSource(log)==='v2'. L'APPELANT les calcule depuis cfg.
//   @param {Map<string, Array>} opts.resasByLogement — Map<logement_id, rows[]>
//     des rows reservations V2 (fournie par le hook use-resas-v2).
//   @param {(lid:string)=>object|null} opts.getLog — résolveur lid→log (pour
//     tarifMenage = forfait ménage par défaut injecté à getResasForMonth).
//   @param {string[]} opts.monthKeys — mois "YYYY-MM" à projeter (l'appelant
//     fournit au minimum le mois courant + le mois suivant + tous les mois
//     présents dans `data`).
//   @param {Array} opts.invoicesRaw — lignes brutes table invoices (gel).
//   @param {(mk:string)=>string} opts.monthKeyToLabel — convertit "YYYY-MM" en
//     libellé "Mai 2026" (pour findBlockingCommission, qui matche meta.mois).
//   @param {object} [opts.menageOverrides] — { [lid]: { [codeStable]: number } }
//     overrides ménage manuels par logement (défaut {}).
//   @param {object} [opts.montantOverrides] — { [lid]: { [codeStable]: number } }
//     overrides MONTANT net hôte manuels par logement (défaut {}). Appliqués
//     APRÈS le ménage (ordre déterministe surcharge).
//   @param {object|Map} [opts.skippedSink] — collecteur des skipped[] par
//     (lid, mk) pour l'UI M3.5. Objet : skippedSink[lid][mk] = skipped[].
//   @param {object|Map} [opts.tombstoneSink] — collecteur des résas RETIRÉES par
//     suppression V2 (Lot 2), par (lid, mk), pour l'affichage admin grisé +
//     bouton Restaurer. Objet : tombstoneSink[lid][mk] = removed[].
// @returns {object} data projeté (ou `data` original par référence).
export function projectDataForFacturation(data, opts = {}) {
  const {
    v2Lids,
    resasByLogement,
    getLog,
    monthKeys,
    invoicesRaw,
    monthKeyToLabel,
    menageOverrides,
    montantOverrides,
    skippedSink,
    tombstoneSink,
  } = opts

  // COURT-CIRCUIT PROUVABLE : aucun logement basculé → objet identique.
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return data
  if (!data || typeof data !== 'object') return data
  if (!Array.isArray(monthKeys) || monthKeys.length === 0) return data

  const overrides = menageOverrides || {}
  const montOverrides = montantOverrides || {}
  // Lot 2 V2-delete-resa : codes stables tombstonés par lid (depuis
  // data._tombstones, buckets airbnb_codes/booking_refs). Réappliqué à CHAQUE
  // render, tous les mois, AVANT les overrides (ordre déterministe
  // supprimer→déplacer→surcharger posé au Lot 1).
  const tombstonedCodes = collectTombstonedCodes(data, v2Lids)
  // Lot 3 V2-move-resa : déplacements de mois par lid (depuis data._monthOverrides).
  // Ré-imputation EFFECTIVE entre applyTombstones et les overrides (ordre
  // déterministe supprimer→déplacer→surcharger). v2Lids vide → {} (no-op).
  const monthOverridesByLid = collectMonthOverrides(data, v2Lids)
  // Lot 5a PRORATA : specs de répartition pluri-mois par lid (depuis
  // data._prorataSplits). 5e calque, APRÈS montantOverride / AVANT fusion
  // manuelle. Store absent → {} → 0 calque (INERTIE, 0-régression).
  const prorataSplitsByLid = collectProrataSplits(data, v2Lids)
  // Maison Zen (décision Dimitri 03/10) : lignes CSV d'une chambre 'v2' sortie
  // de Beds24, arrivées À PARTIR de sa date de bascule, code absent de la V2.
  // Aucune date de bascule → {} → rien n'est ajouté (INERTIE).
  const csvParLid = collectCsvApresBascule(data, {
    v2Lids,
    resasByLogement,
    bascules: opts.csvBascules,
  })
  // D2 : codes dont la ligne CSV fait foi → retirés des lignes V2 ci-dessous.
  const codesCsvRepris = codesReprisParLeCsv(csvParLid, data)

  // Clone shallow du niveau racine (data) — on ne touchera QUE les mk projetés.
  let nextData = null
  const ensureDataClone = () => {
    if (nextData === null) nextData = { ...data }
    return nextData
  }

  for (const lid of v2Lids) {
    if (!lid) continue
    // D2 (décision Dimitri 03/10) : un code repris par une ligne CSV comptée
    // (arrivée à partir de la bascule) sort de la V2 — la ligne CSV fait foi,
    // jamais de double compte. Aucun code repris → mêmes lignes (INERTIE).
    const rowsV2 =
      resasByLogement && resasByLogement.get ? resasByLogement.get(lid) : null
    const rows =
      Array.isArray(rowsV2) && codesCsvRepris.size > 0
        ? rowsV2.filter((row) => !codesCsvRepris.has(codeV2DeLaLigne(row)))
        : rowsV2
    const log = getLog ? getLog(lid) : null
    const cleaningFeeDefault =
      log && log.tarifMenage != null ? log.tarifMenage : undefined
    const lidOverrides =
      overrides && overrides[lid] && typeof overrides[lid] === 'object'
        ? overrides[lid]
        : null
    const lidMontantOverrides =
      montOverrides && montOverrides[lid] && typeof montOverrides[lid] === 'object'
        ? montOverrides[lid]
        : null
    // Lot 3 V2-move-resa : map des déplacements de ce lid (code → 'YYYY-MM').
    const lidMoveMap =
      monthOverridesByLid && monthOverridesByLid[lid] && typeof monthOverridesByLid[lid] === 'object'
        ? monthOverridesByLid[lid]
        : null
    // Lot 5a PRORATA : map des specs de prorata de ce lid (code → spec).
    const lidProrataMap =
      prorataSplitsByLid && prorataSplitsByLid[lid] && typeof prorataSplitsByLid[lid] === 'object'
        ? prorataSplitsByLid[lid]
        : null

    // ── Pré-calcul projectedByCode (Lot 3, étendu Lot 5a) — UNE fois par lid ──
    // Map(code → { resa, naturalMk }) : projette chaque row à son mois NATUREL
    // PUIS applique le filtre tombstone (une résa supprimée n'est JAMAIS
    // réinjectée par un override/prorata). Source des IN dans applyMonthMoves ET
    // de la résa à proratiser (Lot 5a). GATE étendu : construit aussi quand un
    // prorata est posé pour le lid. Coût ZÉRO si NI move NI prorata (gate false
    // → projectedByCode null → INERTIE).
    let projectedByCode = null
    if (
      (lidMoveMap && Object.keys(lidMoveMap).length > 0) ||
      (lidProrataMap && Object.keys(lidProrataMap).length > 0)
    ) {
      projectedByCode = new Map()
      // Mois naturels distincts présents parmi les rows de ce lid.
      const naturalMks = new Set()
      for (const row of rows || []) {
        if (!row) continue
        const ci = row.start_date || null
        const co = row.end_date || null
        const nat =
          computeInvoiceMonthByNights(ci, co) ||
          (typeof ci === 'string' ? ci.slice(0, 7) : null)
        if (nat) naturalMks.add(nat)
      }
      const lidTombSetPre = tombstonedCodes[lid] || null
      for (const natMk of naturalMks) {
        const { resas: natResas } = getResasForMonth(rows || [], natMk, {
          cleaningFeeDefault,
        })
        const kept = lidTombSetPre ? applyTombstones(natResas, lidTombSetPre) : natResas
        for (const r of kept) {
          if (!r) continue
          const code = r.airbnbCode || r.bookingRef || null
          if (!code) continue
          projectedByCode.set(String(code), { resa: r, naturalMk: natMk })
        }
      }
      // Lignes CSV après bascule : leur mois naturel = leur mois de rangement
      // (comme les manuelles). Sans elles, un déplacement les ferait disparaître.
      const csvDuLid = csvParLid[lid] || {}
      for (const csvMk of Object.keys(csvDuLid)) {
        const keptCsv = lidTombSetPre
          ? applyTombstones(csvDuLid[csvMk], lidTombSetPre)
          : csvDuLid[csvMk]
        for (const r of keptCsv) {
          const code = r.airbnbCode || r.bookingRef || null
          if (!code || projectedByCode.has(String(code))) continue
          projectedByCode.set(String(code), { resa: r, naturalMk: csvMk })
        }
      }
    }

    // ── BLOQUANT B2 — Résolution PRORATA PRÉ-CALCULÉE UNE FOIS PAR CODE ──
    // HORS de la boucle mk : pour chaque code avec spec, on résout parts UNE
    // SEULE fois (déterminisme + perf). Le montant proratisé = montantOverride si
    // défini SINON resa.montant (SCALAIRE résolu UNE fois — M1 : on ne passe
    // JAMAIS la map d'override à resolveProrataParts). XOR move : un code qui a
    // AUSSI un déplacement → incohérent → parts null (résa entière au naturel).
    let prorataResolved = null
    if (lidProrataMap && Object.keys(lidProrataMap).length > 0 && projectedByCode) {
      prorataResolved = {}
      for (const code of Object.keys(lidProrataMap)) {
        const spec = lidProrataMap[code]
        const entry = projectedByCode.get(String(code))
        // Résa absente (tombstonée ou row introuvable) → rien à proratiser.
        if (!entry) continue
        const resaNat = entry.resa
        // Périmètre temporel DATE-BOUNDED : TOUS les mois réellement occupés par
        // le séjour (check-in → dernière nuit), INDÉPENDANT de la fenêtre
        // `monthKeys` (correction chemin argent : un mois occupé hors fenêtre,
        // ex. septembre d'une résa juin→sept, doit recevoir sa part au lieu de
        // la voir fondre dans le résidu du mois voisin).
        const monthsTouched = computeMonthsTouched(resaNat)
        // XOR move : un code déplacé ne peut pas être proratisé (sémantiques
        // incompatibles : le move ré-impute en bloc, le prorata répartit). Fail-
        // closed → parts null → résa entière sur son naturel.
        if (lidMoveMap && Object.prototype.hasOwnProperty.call(lidMoveMap, String(code))) {
          // eslint-disable-next-line no-console
          console.warn('proprio-stats.prorata_xor_move', { lid, code: String(code) })
          prorataResolved[String(code)] = {
            parts: null,
            naturalMk: entry.naturalMk,
            resa: resaNat,
            monthsTouched,
          }
          continue
        }
        // Montant final = montantOverride[code] (si défini) SINON resa.montant.
        const montantFinal =
          lidMontantOverrides &&
          Object.prototype.hasOwnProperty.call(lidMontantOverrides, String(code)) &&
          Number.isFinite(Number(lidMontantOverrides[String(code)]))
            ? Math.round(Number(lidMontantOverrides[String(code)]) * 100) / 100
            : Number(resaNat.montant)
        // Mois gelés parmi les mois touchés (gel P série commission OU V voyageur).
        const frozen = new Set()
        if (Array.isArray(invoicesRaw)) {
          for (const x of monthsTouched) {
            const label = monthKeyToLabel ? monthKeyToLabel(x) : null
            if (!label) continue
            if (
              findBlockingCommission(invoicesRaw, { logId: lid, monthLabel: label }) ||
              findBlockingVoyageurInvoiceForMonth(invoicesRaw, { logId: lid, monthLabel: label })
            ) {
              frozen.add(x)
            }
          }
        }
        const parts = resolveProrataParts(
          resaNat,
          montantFinal,
          spec,
          monthsTouched,
          frozen,
          { lid, code: String(code) }
        )
        prorataResolved[String(code)] = {
          parts,
          naturalMk: entry.naturalMk,
          resa: resaNat,
          monthsTouched,
        }
      }
    }

    // ── effectiveMonthKeys : fenêtre EFFECTIVE de la boucle d'émission ──
    // = monthKeys + tout mois TOUCHÉ par un prorata RÉSOLU (parts non null) hors
    // fenêtre. Sans ces mois, applyProrataSplits ne serait jamais appelé pour
    // eux → leur sous-ligne ne serait pas émise (bug septembre). INERTIE STRICTE :
    // aucun prorata résolu hors fenêtre → effectiveMonthKeys === monthKeys (MÊME
    // RÉFÉRENCE) → sortie byte-identique = 0-régression.
    let effectiveMonthKeys = monthKeys
    if (prorataResolved) {
      const monthKeysSet = new Set(monthKeys)
      const extra = []
      for (const code of Object.keys(prorataResolved)) {
        const entry = prorataResolved[code]
        if (!entry || !entry.parts || !Array.isArray(entry.monthsTouched)) continue
        for (const mk of entry.monthsTouched) {
          if (mk && !monthKeysSet.has(mk) && !extra.includes(mk)) extra.push(mk)
        }
      }
      if (extra.length > 0) {
        effectiveMonthKeys = [...monthKeys, ...extra]
      }
    }

    for (const mk of effectiveMonthKeys) {
      if (!mk) continue

      // ── GEL : facture P déjà émise sur ce (lid, mk) → mois figé. ──
      // monthLabel via le résolveur appelant (matche meta.mois STRICT).
      const monthLabel = monthKeyToLabel ? monthKeyToLabel(mk) : null
      if (monthLabel && Array.isArray(invoicesRaw)) {
        const blocking = findBlockingCommission(invoicesRaw, {
          logId: lid,
          monthLabel,
        })
        if (blocking) continue // mois facturé = figé, projection ignorée.
      }

      // Projection des résas V2 pour ce (lid, mk).
      const { resas: resasBeds24, skipped } = getResasForMonth(rows || [], mk, {
        cleaningFeeDefault,
      })
      // Lignes CSV après bascule de ce mois, AVANT le filtre de suppression et
      // les surcharges (elles les subissent comme les autres, par code).
      // Aucune → MÊME RÉFÉRENCE (0-régression).
      const csvIci = csvParLid[lid] && csvParLid[lid][mk]
      const resas =
        csvIci && csvIci.length > 0 ? [...resasBeds24, ...csvIci] : resasBeds24

      // Collecte skipped[] (M3.5) — exposés, pas affichés ici.
      if (skippedSink && skipped && skipped.length > 0) {
        if (skippedSink instanceof Map) {
          if (!skippedSink.has(lid)) skippedSink.set(lid, {})
          skippedSink.get(lid)[mk] = skipped
        } else if (typeof skippedSink === 'object') {
          if (!skippedSink[lid]) skippedSink[lid] = {}
          skippedSink[lid][mk] = skipped
        }
      }

      // ── Lot 2 V2-delete-resa : FILTRE de suppression, AVANT les overrides ──
      // Retire les résas dont le code stable est tombstoné (suppression admin).
      // GEL : un mois facturé fait `continue` PLUS HAUT (avant ce point) → le
      // filtre ne s'y applique jamais = passé figé préservé (une facture émise a
      // snapshoté ses lignes ; on ne réécrit pas le passé). applyTombstones renvoie
      // la MÊME référence si aucun code n'a matché → byte-identique sans tombstone.
      const lidTombSet = tombstonedCodes[lid]
      let baseResas = resas
      if (lidTombSet) {
        const removedArr = []
        baseResas = applyTombstones(resas, lidTombSet, removedArr)
        // Expose les résas retirées (affichage admin grisé + Restaurer).
        if (tombstoneSink && removedArr.length > 0) {
          if (tombstoneSink instanceof Map) {
            if (!tombstoneSink.has(lid)) tombstoneSink.set(lid, {})
            tombstoneSink.get(lid)[mk] = removedArr
          } else if (typeof tombstoneSink === 'object') {
            if (!tombstoneSink[lid]) tombstoneSink[lid] = {}
            tombstoneSink[lid][mk] = removedArr
          }
        }
      }

      // ── Lot 3 V2-move-resa : DÉPLACER, entre supprimer et surcharger ──
      // Ré-imputation effective : OUT (retire de ce mois les codes pointant
      // ailleurs) + IN (réinjecte ici les codes ciblant mk depuis projectedByCode
      // déjà filtrée tombstone — une supprimée n'est jamais réinjectée). Renvoie
      // la MÊME référence si aucun déplacement pour ce lid (projectedByCode null).
      // Les overrides ménage/montant (ci-dessous) s'appliquent PAR CODE → suivent
      // la résa dans son mois cible.
      if (projectedByCode) {
        baseResas = applyMonthMoves(baseResas, mk, lidMoveMap, projectedByCode)
      }

      // ── Surcharges (ordre déterministe, clé stable uniquement) ──
      // Préfigure les Lots 2/3/4 (supprimer → déplacer → surcharger) : le filtre
      // de suppression (ci-dessus) d'abord, le déplacement (ci-dessus), puis le
      // ménage, puis le montant net hôte. Chaque applyXOverrides renvoie MÊME
      // référence si aucun override n'a matché → byte-identique sans override.
      let projectedResas = lidOverrides
        ? applyMenageOverrides(baseResas, lidOverrides)
        : baseResas
      projectedResas = lidMontantOverrides
        ? applyMontantOverrides(projectedResas, lidMontantOverrides)
        : projectedResas

      // ── Lot 5a PRORATA : 5e calque, APRÈS montantOverride / AVANT fusion ──
      // Répartit le montant d'une résa choisie sur les mois qu'elle touche
      // (parts pré-résolues B2). MÊME RÉFÉRENCE si aucun prorata résolu pour ce
      // lid (prorataResolved null/vide) → byte-identique sans prorata (INERTIE).
      if (prorataResolved) {
        projectedResas = applyProrataSplits(projectedResas, mk, prorataResolved)
      }

      // ── Substitution CIBLÉE : remplace UNIQUEMENT data[mk][lid].reservations.
      // Clones shallow par niveau touché — jamais de mutation de l'original.
      const dd = ensureDataClone()
      const monthOrig = data[mk] || {}
      // Clone du mois UNE SEULE FOIS : tant que dd[mk] pointe encore sur le mois
      // d'origine, on le clone ; ensuite (autre lid sur le même mk) on réutilise
      // le clone et on y ajoute le lid courant.
      if (dd[mk] === data[mk]) {
        dd[mk] = { ...monthOrig }
      }
      const lidOrig = monthOrig[lid] || {}
      // ── Lot 4 V2-create-manual : FUSION des résas manuelles KV ──
      // Une résa manuelle (source:'manual') vit dans le bucket physique KV
      // data[mk][lid].reservations ; la projection l'AJOUTE aux résas Beds24
      // projetées (au lieu de remplacer). 0 manuelle → MÊME RÉFÉRENCE
      // (projectedResas) → sortie byte-identique = 0-régression sur le vrai
      // chemin prod (v2Lids=[lc1,lc2,lc3], résas legacy KV sans `source`).
      const manualResas = collectManualResasForMonth(lidOrig.reservations, mk)
      const mergedReservations =
        manualResas.length === 0
          ? projectedResas // 0 manuelle → MÊME RÉFÉRENCE (0-régression)
          : [...projectedResas, ...manualResas] // Beds24 d'abord, manuelles ensuite
      dd[mk][lid] = { ...lidOrig, reservations: mergedReservations }
    }
  }

  // Aucun mk × lid touché (ex. tous gelés) → renvoyer l'original (référence).
  return nextData === null ? data : nextData
}
